import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface FocusSeshState {
  linkedClassroomId: number | null
  isTimed: boolean
  durationMinutes: number
  durationSeconds: number
  isNoCheckIns: boolean | null
  isAutoCheckIns: boolean | null
  checkInFrequency: string | null
  initialized: boolean
  checkInTimes: number[] | null
  checkInSound: string | null
  startAndEndSound: string | null
  soundtrackAllowed: boolean | null
  soundtrack: string | null
  voiceGuidance: string | null
}

export const selectFocusSesh = (state: any) => state.focusSesh
export const selectLinkedClassroomId = (state: any) => state.focusSesh.linkedClassroomId
export const selectIsTimed = (state: any) => state.focusSesh.isTimed
export const selectDurationMinutes = (state: any) => state.focusSesh.durationMinutes
export const selectDurationSeconds = (state: any) => state.focusSesh.durationSeconds
export const selectIsNoCheckIns = (state: any) => state.focusSesh.isNoCheckIns
export const selectIsAutoCheckIns = (state: any) => state.focusSesh.isAutoCheckIns
export const selectCheckInFrequency = (state: any) => state.focusSesh.checkInFrequency
export const selectCheckInSound = (state: any) => state.focusSesh.checkInSound
export const selectStartAndEndSound = (state: any) => state.focusSesh.startAndEndSound
export const selectSeshInitialized = (state: any) => state.focusSesh.initialized
export const selectCheckInTimes = (state: any) => state.focusSesh.checkInTimes
export const selectSoundtrack = (state: any) => state.focusSesh.soundtrack
export const selectSoundtrackAllowed = (state: any) => state.focusSesh.soundtrackAllowed
export const selectVoiceGuidance = (state: any) => state.focusSesh.voiceGuidance

// TODO add to tests if needed
export const defaultCustomCheckInRange = '3-7 Minutes'

const initialState: FocusSeshState = {
  linkedClassroomId: null,
  isTimed: true,
  durationMinutes: 0,
  durationSeconds: 0,
  isNoCheckIns: null,
  isAutoCheckIns: null,
  checkInFrequency: null,
  initialized: false,
  checkInTimes: null,
  checkInSound: null,
  startAndEndSound: null,
  soundtrackAllowed: null,
  soundtrack: null,
  voiceGuidance: null,
}

const focusSeshSlice = createSlice({
  name: 'focusSesh',
  initialState,
  reducers: {
    setLinkedClassroomId: (state, {
      payload: {
        linkedClassroomId,
      }
    }: PayloadAction<{ linkedClassroomId: number | null }>) => {
      state.linkedClassroomId = linkedClassroomId
    },
    setIsTimed: (state, {
      payload: {
        isTimed,
      },
    }: PayloadAction<{ isTimed: boolean }>) => {
      state.initialized = false
      state.isTimed = isTimed
    },
    setDurationMinutes: (state, {
      payload: {
        durationMinutes,
      },
    }: PayloadAction<{ durationMinutes: number }>) => {
      state.initialized = false
      state.durationMinutes = durationMinutes
    },
    setDurationSeconds: (state, {
      payload: {
        durationSeconds,
      },
    }: PayloadAction<{ durationSeconds: number }>) => {
      state.initialized = false
      state.durationSeconds = durationSeconds
    },
    setIsNoCheckIns: (state, {
      payload: {
        isNoCheckIns,
      },
    }: PayloadAction<{ isNoCheckIns: boolean | null }>) => {
      state.initialized = false
      state.isNoCheckIns = isNoCheckIns
    },
    setIsAutoCheckIns: (state, {
      payload: {
        isAutoCheckIns,
      },
    }: PayloadAction<{ isAutoCheckIns: boolean | null }>) => {
      state.initialized = false
      state.isAutoCheckIns = isAutoCheckIns
    },
    setCheckInFrequency: (state, {
      payload: {
        checkInFrequency,
      },
    }: PayloadAction<{ checkInFrequency: string | null }>) => {
      state.initialized = false
      state.checkInFrequency = checkInFrequency
    },
    setStartAndEndSound: (state, {
      payload: {
        startAndEndSound,
      },
    }: PayloadAction<{ startAndEndSound: string | null }>) => {
      state.initialized = false
      state.startAndEndSound = startAndEndSound
    },
    setCheckInSound: (state, {
      payload: {
        checkInSound,
      },
    }: PayloadAction<{ checkInSound: string | null }>) => {
      state.initialized = false
      state.checkInSound = checkInSound
    },
    setSoundtrackAllowed: (state, {
      payload: {
        soundtrackAllowed,
      },
    }: PayloadAction<{ soundtrackAllowed: boolean | null }>) => {
      state.initialized = false
      state.soundtrackAllowed = soundtrackAllowed
    },
    setSoundtrack: (state, {
      payload: {
        soundtrack,
      },
    }: PayloadAction<{ soundtrack: string | null }>) => {
      state.soundtrack = soundtrack
    },
    setVoiceGuidance: (state, {
      payload: {
        voiceGuidance,
      },
    }: PayloadAction<{ voiceGuidance: string | null }>) => {
      state.voiceGuidance = voiceGuidance
    },
    setInitialized: (state, {
      payload: checkInTimes,
    }: PayloadAction<number[]>) => {
      state.checkInTimes = checkInTimes
      state.initialized = true
    },
    // Note: Only used to add next check-in time during personal sesh with auto check-ins
    addNextCheckInTime: (state, {
      payload: newCheckInTime,
    }: PayloadAction<number>) => {
      state.checkInTimes.push(newCheckInTime)
    }
  },
})

export const {
  setLinkedClassroomId,
  setIsTimed,
  setDurationSeconds,
  setDurationMinutes,
  setIsNoCheckIns,
  setIsAutoCheckIns,
  setCheckInFrequency,
  setInitialized,
  setStartAndEndSound,
  setCheckInSound,
  setSoundtrackAllowed,
  setSoundtrack,
  setVoiceGuidance,
  addNextCheckInTime,
} = focusSeshSlice.actions

export default focusSeshSlice.reducer
